import { Box, Card, CardContent, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { alpha, useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';

export type AiModerationSignal = {
    name: string;
    result: string;
};

export type AiModerationChangedField = {
    propertyName: string;
    oldValue: string;
    newValue: string;
};

export type AiModeration = {
    state: string;
    attempts: number;
    startedAt?: string | null;
    finishedAt?: string | null;
    nextRetryAt?: string | null;
    decision?: string | null;
    confidence?: number | null;
    publicReason?: string | null;
    internalReason?: string | null;
    signals: AiModerationSignal[];
    changedFields?: AiModerationChangedField[] | null;
};

type AiModerationResultCardProps = {
    aiModeration?: AiModeration | null;
    sx?: SxProps<Theme>;
};

const STATE_LABELS: Record<string, string> = {
    PENDING: 'Ожидает проверки',
    PROCESSING: 'Проверяется',
    DONE: 'Проверка завершена',
    FAILED: 'Ошибка проверки',
    NEED_MANUAL_CHECK: 'Нужна ручная проверка'
};

const STATE_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
    PENDING: 'default',
    PROCESSING: 'info',
    DONE: 'success',
    FAILED: 'error',
    NEED_MANUAL_CHECK: 'warning'
};

const DECISION_LABELS: Record<string, string> = {
    APPROVE_ACTIVE: 'Опубликовать',
    SEND_TO_EXPERT: 'Отправить экспертам',
    NEED_CORRECT: 'Требуется исправление',
    REJECT: 'Отказать в публикации',
    MANUAL_REVIEW: 'Требуется проверка человеком'
};

const DECISION_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
    APPROVE_ACTIVE: 'success',
    SEND_TO_EXPERT: 'default',
    NEED_CORRECT: 'warning',
    REJECT: 'error',
    MANUAL_REVIEW: 'info'
};

const formatText = (value?: string | null) => {
    const normalized = value?.trim();
    return normalized || 'Не указано';
};

const formatNextRetryAt = (value?: string | null) => {
    if (!value) {
        return 'Не требуется';
    }
    return formatDateTime(value);
};

const formatDateTime = (value?: string | null) => {
    if (!value) {
        return 'Не указано';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });
};

const formatDuration = (startedAt?: string | null, finishedAt?: string | null) => {
    if (startedAt && !finishedAt) {
        return 'В процессе';
    }
    if (!startedAt || !finishedAt) {
        return 'Не указано';
    }

    const started = new Date(startedAt).getTime();
    const finished = new Date(finishedAt).getTime();
    if (Number.isNaN(started) || Number.isNaN(finished) || finished < started) {
        return 'Не указано';
    }

    const totalSeconds = Math.round((finished - started) / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const parts = [
        hours ? `${hours} ч` : null,
        minutes ? `${minutes} мин` : null,
        seconds || (!hours && !minutes) ? `${seconds} сек` : null
    ].filter(Boolean);

    return parts.join(' ');
};

const clampConfidence = (value?: number | null) => {
    if (!Number.isFinite(value)) {
        return null;
    }
    return Math.max(0, Math.min(100, Math.round(Number(value) * 100)));
};

export const AiModerationResultCard = ({ aiModeration, sx }: AiModerationResultCardProps) => {
    const theme = useTheme();

    if (!aiModeration) {
        return null;
    }

    const isDarkMode = theme.palette.mode === 'dark';
    const normalizedState = formatText(aiModeration.state).toUpperCase();
    const stateLabel = STATE_LABELS[normalizedState] || formatText(aiModeration.state);
    const stateColor = STATE_COLORS[normalizedState] || 'default';
    const normalizedDecision = formatText(aiModeration.decision).toUpperCase();
    const decisionLabel = DECISION_LABELS[normalizedDecision] || formatText(aiModeration.decision);
    const decisionColor = DECISION_COLORS[normalizedDecision] || 'default';
    const confidencePercent = clampConfidence(aiModeration.confidence);
    const signals = Array.isArray(aiModeration.signals) ? aiModeration.signals : [];
    const changedFields = Array.isArray(aiModeration.changedFields) ? aiModeration.changedFields : [];

    const metricTileSx = {
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: alpha(theme.palette.info.main, isDarkMode ? 0.28 : 0.18),
        bgcolor: alpha(theme.palette.background.paper, isDarkMode ? 0.54 : 0.72),
        minWidth: 0
    };
    const cardSx: SxProps<Theme> = [
        {
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 3,
            border: '1px solid',
            borderColor: alpha(theme.palette.info.main, isDarkMode ? 0.48 : 0.28),
            background: isDarkMode
                ? `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.22)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 48%, ${alpha(theme.palette.secondary.main, 0.18)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.info.light, 0.32)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 48%, ${alpha(theme.palette.secondary.light, 0.18)} 100%)`,
            boxShadow: isDarkMode ? `0 18px 44px ${alpha(theme.palette.common.black, 0.32)}` : `0 18px 44px ${alpha(theme.palette.info.main, 0.14)}`,
            '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: `radial-gradient(circle at top right, ${alpha(theme.palette.info.main, isDarkMode ? 0.18 : 0.16)}, transparent 36%)`,
                pointerEvents: 'none'
            }
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
    ];

    return (
        <Card sx={cardSx}>
            <CardContent sx={{ position: 'relative', zIndex: 1, p: { xs: 2.5, md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'info.main',
                                bgcolor: alpha(theme.palette.info.main, isDarkMode ? 0.22 : 0.14),
                                boxShadow: `0 0 24px ${alpha(theme.palette.info.main, isDarkMode ? 0.28 : 0.2)}`,
                                flexShrink: 0
                            }}
                        >
                            <AutoAwesomeIcon />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="overline" sx={{ color: 'info.main', letterSpacing: '0.08em', fontWeight: 800 }}>
                                Rocket AI Moderation
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                                Результат первичной проверки
                            </Typography>
                        </Box>
                    </Box>
                    <Chip label={stateLabel} color={stateColor} variant="filled" />
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
                        gap: 1.5,
                        mb: 2.5
                    }}
                >
                    <Box sx={metricTileSx}>
                        <Typography variant="caption" color="text.secondary">Попытки</Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{Number.isFinite(aiModeration.attempts) ? aiModeration.attempts : 'Не указано'}</Typography>
                    </Box>
                    <Box sx={metricTileSx}>
                        <Typography variant="caption" color="text.secondary">Длительность</Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{formatDuration(aiModeration.startedAt, aiModeration.finishedAt)}</Typography>
                    </Box>
                    <Box sx={metricTileSx}>
                        <Typography variant="caption" color="text.secondary">Следующая попытка</Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, overflowWrap: 'anywhere' }}>{formatNextRetryAt(aiModeration.nextRetryAt)}</Typography>
                    </Box>
                    <Box sx={metricTileSx}>
                        <Typography variant="caption" color="text.secondary">Решение</Typography>
                        <Box sx={{ mt: 0.75 }}>
                            <Chip label={decisionLabel} color={decisionColor} size="small" />
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                        <Typography variant="body2" color="text.secondary">Уверенность</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {confidencePercent === null ? 'Не указано' : `${confidencePercent}%`}
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={confidencePercent ?? 0}
                        sx={{
                            height: 8,
                            borderRadius: 999,
                            bgcolor: alpha(theme.palette.info.main, isDarkMode ? 0.16 : 0.1),
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 999,
                                background: `linear-gradient(90deg, ${theme.palette.info.main}, ${theme.palette.secondary.main})`
                            }
                        }}
                    />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5, mb: 2.5 }}>
                    <Box sx={metricTileSx}>
                        <Typography variant="caption" color="text.secondary">Причина для пользователя</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{formatText(aiModeration.publicReason)}</Typography>
                    </Box>
                    <Box sx={metricTileSx}>
                        <Typography variant="caption" color="text.secondary">Внутренняя причина</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{formatText(aiModeration.internalReason)}</Typography>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                        Сигналы
                    </Typography>
                    {signals.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Сигналы не указаны</Typography>
                    ) : (
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                            {signals.map((signal, index) => (
                                <Chip
                                    key={`${signal.name || 'signal'}-${signal.result || 'result'}-${index}`}
                                    label={`${formatText(signal.name)}: ${formatText(signal.result)}`}
                                    variant="outlined"
                                    color="info"
                                    sx={{
                                        maxWidth: '100%',
                                        '& .MuiChip-label': {
                                            overflowWrap: 'anywhere',
                                            whiteSpace: 'normal'
                                        }
                                    }}
                                />
                            ))}
                        </Stack>
                    )}
                </Box>

                <Box sx={{ mt: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                        Измененные поля
                    </Typography>
                    {changedFields.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">ИИ не изменял поля объявления</Typography>
                    ) : (
                        <Box sx={{ display: 'grid', gap: 1 }}>
                            {changedFields.map((field, index) => (
                                <Box
                                    key={`${field.propertyName || 'field'}-${index}`}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: alpha(theme.palette.secondary.main, isDarkMode ? 0.28 : 0.18),
                                        bgcolor: alpha(theme.palette.background.paper, isDarkMode ? 0.5 : 0.68)
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary">
                                        {formatText(field.propertyName)}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                            gap: 1,
                                            mt: 0.75
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Было</Typography>
                                            <Typography variant="body2" sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
                                                {formatText(field.oldValue)}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Стало</Typography>
                                            <Typography variant="body2" sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap', fontWeight: 700 }}>
                                                {formatText(field.newValue)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};
